import type { MockSectionCustomerInput } from './mockSectionData.util';
import { mockSectionData } from './mockSectionData.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { pickReportFieldValue } from './reportFieldSource.util';
import {
  buildWhatMembersNeedFulfillmentStatsFromTrend,
  coerceWhatMembersNeedFulfillmentStats,
  coerceWhatMembersNeedIdentifiedVsFulfilled,
  coerceWhatMembersNeedTopNeeds,
  whatMembersNeedFulfillmentStatsHasData,
  whatMembersNeedHasWarehouseData,
  whatMembersNeedTrendHasData,
} from './whatMembersNeedSnapshot.util';
import { isWarehouseWhoWereReachingSnapshot } from './whoWereReachingSnapshotDetect.util';

/**
 * Normalizes what-members-need snapshots with mock fallbacks for missing warehouse fields.
 * @param snapshot - Stored or live snapshot JSON.
 * @param customer - Customer context for mock fallback fields.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with merged data and per-field `fieldSources` metadata.
 */
export const normalizeWhatMembersNeedSnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const mockBase = mockSectionData(
    'what-members-need',
    customer,
    reportingPeriod,
  );

  const isWarehouse = isWarehouseWhoWereReachingSnapshot(snapshot);

  const warehouseTopNeeds = isWarehouse
    ? coerceWhatMembersNeedTopNeeds(snapshot.topNeeds)
    : [];
  const mockTopNeeds = coerceWhatMembersNeedTopNeeds(mockBase.topNeeds);
  const topNeedsPick = pickReportFieldValue(
    warehouseTopNeeds,
    mockTopNeeds,
    warehouseTopNeeds.length > 0,
  );

  const warehouseTrend = isWarehouse
    ? coerceWhatMembersNeedIdentifiedVsFulfilled(snapshot.identifiedVsFulfilled)
    : [];
  const mockTrend = coerceWhatMembersNeedIdentifiedVsFulfilled(
    mockBase.identifiedVsFulfilled,
  );
  const trendPick = pickReportFieldValue(
    warehouseTrend,
    mockTrend,
    whatMembersNeedTrendHasData(warehouseTrend),
  );

  const warehouseStats = isWarehouse
    ? coerceWhatMembersNeedFulfillmentStats(snapshot.fulfillmentStats)
    : null;
  const mockStats = buildWhatMembersNeedFulfillmentStatsFromTrend(mockTrend);
  const statsPick = pickReportFieldValue(
    warehouseStats,
    mockStats,
    whatMembersNeedFulfillmentStatsHasData(warehouseStats),
  );

  const fieldSources: ReportSectionFieldSources = {
    topNeeds: topNeedsPick.source,
    identifiedVsFulfilled: trendPick.source,
    fulfillmentStats: statsPick.source,
    insights: 'mock',
  };

  return {
    ...mockBase,
    ...snapshot,
    topNeeds: topNeedsPick.value,
    identifiedVsFulfilled: trendPick.value,
    fulfillmentStats: statsPick.value,
    fieldSources,
    snapshotSource: isWarehouseWhoWereReachingSnapshot(snapshot)
      ? snapshot.snapshotSource
      : undefined,
  };
};

export { whatMembersNeedHasWarehouseData };
